import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { InlineRestrictionsList } from "@/features/InlineRestrictions";
import {
  BorderRadiusOption,
  PublicTimeSlotCard,
} from "@whoosh/design-system/src/apps/PatronApp/screens/teesheet/components/TeeSheet/TimeSlotCard/";
import { Box } from "@whoosh/design-system/src/components/primitive";

import { PublicTimeSlotCardWrapper_Timeslot$key } from "./__generated__/PublicTimeSlotCardWrapper_Timeslot.graphql";

type PublicTimeSlotCardWrapperProps = {
  availableSpots: number;
  isRequestAvailable: boolean;
  onPress?: () => void;
  borderRadius?: {
    top?: BorderRadiusOption;
    bottom?: BorderRadiusOption;
  };
  location?: string;
  timeSlotRef: PublicTimeSlotCardWrapper_Timeslot$key;
  time?: string;
  position: "first" | "last" | "middle" | "only";
};
export const PublicTimeCardWrapper = ({
  availableSpots,
  isRequestAvailable,
  onPress,
  borderRadius,
  location,
  timeSlotRef,
  time,
  position,
}: PublicTimeSlotCardWrapperProps) => {
  const timeSlot = useFragment(
    PublicTimeSlotCardWrapperFragmentString,
    timeSlotRef
  );
  const courseLayouts =
    timeSlot?.permittedCourseLayouts?.edges?.flatMap((edge) =>
      edge?.node ? [edge.node] : []
    ) || [];

  const nineHolesAllowed = courseLayouts.some((l) => l.holeCount === "NINE");
  const eighteenHolesAllowed = courseLayouts.some(
    (l) => l.holeCount === "EIGHTEEN"
  );

  const courseLayoutCodes = courseLayouts
    .sort()
    .reverse()
    .map((l) => l.code);

  const rates = timeSlot?.rates?.flatMap((r) => (r ? [r] : [])) || [];
  const transportationTypes =
    timeSlot?.transportationTypes?.flatMap((tt) => (tt ? [tt] : [])) || [];

  const combinedPrices = rates.flatMap((r) => {
    const rateNineHolePrice = r.nineHolePrice || 0;
    const rateEighteenHolePrice = r.eighteenHolePrice || 0;

    const withNineHoleTransportationPrices = nineHolesAllowed
      ? transportationTypes.map(
          (t) => (t.nineHolePrice || 0) + rateNineHolePrice
        )
      : [];

    const withEighteenHoleTransportationPrices = eighteenHolesAllowed
      ? transportationTypes.map(
          (t) => (t.eighteenHolePrice || 0) + rateEighteenHolePrice
        )
      : [];

    return [
      ...withNineHoleTransportationPrices,
      ...withEighteenHoleTransportationPrices,
    ];
  });

  const showCardMargin =
    (position === "last" || position === "only") && (availableSpots ?? 0) > 0;

  return (
    <Box mb={showCardMargin ? 2 : 0}>
      <PublicTimeSlotCard
        time={time}
        onPress={onPress}
        borderRadius={borderRadius}
        isRequestAvailable={isRequestAvailable}
        availableSpots={availableSpots}
        location={location}
        layouts={courseLayoutCodes}
        prices={combinedPrices}
        restrictionList={<InlineRestrictionsList timeSlotRef={timeSlot} />}
      />
    </Box>
  );
};

export const PublicTimeSlotCardWrapperFragmentString = graphql`
  fragment PublicTimeSlotCardWrapper_Timeslot on TimeSlot
  @argumentDefinitions(membershipTypeId: { type: "ID!", defaultValue: "" }) {
    ...InlineRestrictionsList_timeSlot
    permittedCourseLayouts(first: 10, memberVisible: true) {
      edges {
        node {
          id
          code
          name
          memberVisible
          enabled
          default
          holeCount
        }
      }
    }
    playerRateForMembershipType(
      membershipTypeId: $membershipTypeId
      type: CUSTOMER
    ) {
      rate {
        id
        name
        type
        code
      }
      nineHolePrice: totalGolfPrice(holeCount: NINE)
      eighteenHolePrice: totalGolfPrice(holeCount: EIGHTEEN)
    }
    transportationTypes(memberVisible: true) {
      id
      nineHolePrice: totalPrice(holeCount: NINE)
      eighteenHolePrice: totalPrice(holeCount: EIGHTEEN)
    }
    rates: playerRates(
      highestPrecedentOnly: true
      nonPlayingHost: false
      type: CUSTOMER
    ) {
      rate {
        id
        name
        type
        code
      }
      nineHolePrice: totalGolfPrice(holeCount: NINE)
      eighteenHolePrice: totalGolfPrice(holeCount: EIGHTEEN)
    }
  }
`;
